<template>
  <entity-edit-dialog
    @submit="submit"
    @close="close"
    v-model="visible"
    ref="createPartDialog"
    :dialog-title="dialogTitle"
    :submitting="submitting"
  >
    <template v-slot:contentCard>
      <v-card-text>
        <div class="my-5" v-if="error && !submitting">
          <p class="alertText">{{ $t('common.errors.error') }}</p>
          <p>{{ error }}</p>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold required">{{ $t('part.name') }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="partFormData.name"
                ref="nameInput"
                outlined
                dense
                hide-details="auto"
                :rules="[(v) => !!v || $t('validation.required')]"
                required
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('part.identification') }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="partFormData.identification"
                ref="identificationInput"
                outlined
                dense
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{
                $t('part.supplierIdentification')
              }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="partFormData.manufacturerIdentification"
                ref="manufacturerIdentificationInput"
                outlined
                dense
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('part.manufacturer') }}</span>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="partFormData.manufacturer"
                ref="manufacturerInput"
                outlined
                dense
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('part.storageLocation') }}</span>
            </v-col>
            <v-col cols="12" sm="8">
              <v-textarea
                v-model="partFormData.storageLocation"
                rows="3"
                outlined
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('part.externalLink') }}</span>
            </v-col>
            <v-col cols="12" sm="9">
              <v-text-field
                v-model="partFormData.externalLink"
                ref="externalLinkInput"
                outlined
                dense
                hide-details="auto"
                autocomplete="false"
              />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="5" sm="3">
              <span class="span-bold">{{ $t('part.trackingUnit') }}</span>
            </v-col>
            <v-col cols="6" sm="2">
              <TrackingUnitSelect v-model="partFormData.TrackingUnit" dense />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col offset="5" cols="3" offset-sm="3" sm="2">
              <v-text-field
                type="number"
                class="right-align-number-input"
                min="0"
                max="3"
                outlined
                dense
                hide-details="auto"
                v-model="partFormData.decimals"
                @input="updateMWI"
                autocomplete="false"
              />
            </v-col>
            <v-col cols="4" sm="3"
              ><span class="span-bold">{{ $t('part.decimals') }}</span></v-col
            >
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="5" sm="3">
              <span class="span-bold">{{
                $t('part.minimumWarehouseInventory')
              }}</span>
            </v-col>
            <v-col cols="5" sm="3">
              <v-text-field
                type="number"
                class="right-align-number-input"
                min="0"
                :step="decimalStep"
                outlined
                dense
                hide-details="auto"
                v-model="partFormData.inventoryMinimum"
                autocomplete="false"
              />
            </v-col>
            <v-col cols="2"
              ><span class="span-bold">{{ unitName }}</span></v-col
            >
          </v-row>
        </div>

        <div class="mb-5">
          <v-row dense>
            <v-col cols="12" sm="9" offset-sm="3" class="fill-height">
              <span class="span-bold">
                {{ $t('part.inventoryText') }}
              </span>
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="4" sm="3">
              <span class="span-bold">{{ $t('part.timeOfDelivery') }}</span>
            </v-col>
            <v-col cols="5" sm="3">
              <v-text-field
                type="number"
                class="right-align-number-input"
                min="0"
                outlined
                dense
                hide-details="auto"
                v-model="partFormData.deliveryTimeDays"
                autocomplete="false"
              />
            </v-col>
            <v-col cols="3" sm="1"
              ><span class="span-bold">{{ $t('part.days') }}</span></v-col
            >
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="4" sm="3">
              <span class="span-bold">{{ $t('part.price') }}</span>
            </v-col>
            <v-col cols="5" sm="3">
              <v-text-field
                type="number"
                class="right-align-number-input"
                min="0"
                step=".01"
                outlined
                dense
                hide-details="auto"
                v-model="partFormData.price"
                autocomplete="false"
              />
            </v-col>
            <v-col cols="3" sm="2"
              ><span class="span-bold">{{ priceUnit }}</span></v-col
            >
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{ $t('part.suppliers') }}</span>
            </v-col>
            <v-col cols="12" sm="9">
              <MultipleSuppliersSelect v-model="partFormData.suppliers" />
            </v-col>
          </v-row>
        </div>

        <div class="my-5">
          <v-row align="center" dense>
            <v-col cols="12" sm="3">
              <span class="span-bold">{{
                $t('plannedService.documents')
              }}</span>
            </v-col>
            <v-col cols="12" sm="9">
              <documents-select v-model="documents" />
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </template>
  </entity-edit-dialog>
</template>

<script>
import EntityEditDialog from '@/components/entity-crud/EntityEditDialog';
import i18n from '@/i18n';
import PartsService from '@/services/PartsService';
import DocumentsSelect from '@/components/inputs/DocumentsSelect';
import MultipleSuppliersSelect from '@/components/inputs/MultipleSuppliersSelect';
import TrackingUnitSelect from '@/components/inputs/TrackingUnitSelect';
import { PART_UNIT_NAMES } from '../../utils/PartUnits';

// WARNING: Do not use this as an example on how to do anything

function createData() {
  return {
    name: null,
    identification: '',
    manufacturerIdentification: '',
    manufacturer: '',
    storageLocation: '',
    externalLink: '',
    TrackingUnit: 1,
    decimals: null,
    inventoryMinimum: null,
    deliveryTimeDays: null,
    price: null,
    suppliers: [],
  };
}

export default {
  name: 'PartFormDialog',

  components: {
    EntityEditDialog,
    TrackingUnitSelect,
    DocumentsSelect,
    MultipleSuppliersSelect,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    partData: {
      type: Object,
    },
  },

  data: () => ({
    partFormData: createData(),
    documents: [],
    submitting: false,
    error: null,
  }),

  computed: {
    decimalStep() {
      if (!this.partFormData.decimals || this.partFormData.decimals < 1) {
        return undefined;
      }

      let nulls = '';
      for (let index = 1; index < this.partFormData.decimals; index++) {
        nulls += '0';
      }
      const step = `.${nulls}1`;

      return step;
    },

    unitName() {
      return this.$options.filters.unit(
        PART_UNIT_NAMES[this.partFormData.TrackingUnit - 1],
      );
    },

    priceUnit() {
      return `€ / ${this.unitName}`;
    },

    mapToItem(part) {
      return {
        text: this.$options.filters.part(part),
        value: part,
      };
    },
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    dialogTitle() {
      if (this.partFormData?.id) {
        return i18n.t('part.dialog.titleEdit');
      } else {
        return i18n.t('part.dialog.titleAdd');
      }
    },
  },

  methods: {
    updateMWI() {
      if (this.partFormData.inventoryMinimum) {
        if (this.partFormData.decimals > 0) {
          this.partFormData.inventoryMinimum = parseFloat(
            this.partFormData.inventoryMinimum,
          )
            .toFixed(this.partFormData.decimals)
            .toString();
        } else {
          this.partFormData.inventoryMinimum = Math.round(
            this.partFormData.inventoryMinimum,
          ).toString();
        }
      }
    },

    close() {
      this.partFormData = createData();
      this.partFormData.suppliers = [];
      this.documents = [];
      this.submitting = false;
      this.error = null;

      this.$emit('dialogClosed');
    },

    submit() {
      if (this.submitting) {
        return;
      }
      this.error = null;

      if (!this.$refs.createPartDialog.$refs.form.validate()) {
        this.$refs.createPartDialog.showFormHasValidationErrorsMessage(
          i18n.t('plannedService.requiredInformationMissing'),
        );
        return;
      }

      const part = {
        name: this.partFormData.name,
        identification: this.partFormData.identification,
        manufacturerIdentification: this.partFormData
          .manufacturerIdentification,
        manufacturer: this.partFormData.manufacturer,
        inventoryMinimum: this.partFormData.inventoryMinimum,
        price: this.partFormData.price,
        deliveryTimeDays: this.partFormData.deliveryTimeDays,
        storageLocation: this.partFormData.storageLocation,
        externalUrl: this.partFormData.externalLink,
        trackingUnit: {
          unit: PART_UNIT_NAMES[this.partFormData.TrackingUnit - 1],
          decimals:
            this.partFormData.decimals > 0 ? this.partFormData.decimals : 0,
        },
      };

      const partDTO = {
        part,
        suppliers: this.partFormData.suppliers,
        documents: this.documents,
      };

      this.submitting = true;
      PartsService.addPart(partDTO)
        .then((result) => {
          this.$emit('selectedPart', result.data);
          this.close();
        })
        .catch((err) => {
          this.error =
            err.response.data.message ||
            err.message ||
            err.toString() ||
            'Some Network Error';

          this.$refs.nameInput.focus();
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.required:after {
  content: ' *';
  color: red;
}

.span-bold {
  font-weight: bolder;
}

.alertText {
  color: red;
  font-weight: bolder;
}
</style>
